<template>
  <Transition name="fade" appear>
    <div
      v-if="show"
      class="popup-shell-overlay"
      :class="{ 'is-forced-fullscreen': forceFullscreen }"
      :style="{ zIndex }"
      @click.self="attemptClose"
    >
      <div class="popup-shell-viewport" :class="{ 'is-forced-fullscreen': forceFullscreen }" @click.self="attemptClose">
        <div class="popup-shell-card" :class="[containerClass, { 'is-forced-fullscreen': forceFullscreen }]" :style="popupCardStyle">
          <button
            v-if="!hideCloseButton"
            class="popup-shell-close"
            type="button"
            :aria-label="closeAriaLabel"
            @click="attemptClose"
          >
            &times;
          </button>

          <div class="popup-shell-body">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useEscapeKey } from '../../composables/useEscapeKey';

type ClassValue = string | Record<string, boolean> | Array<string | Record<string, boolean>>;

interface Props {
  readonly show: boolean;
  readonly disableClose?: boolean;
  readonly hideCloseButton?: boolean;
  readonly closeAriaLabel?: string;
  readonly zIndex?: number;
  readonly escapePriority?: number;
  readonly maxWidth?: number | string;
  readonly containerClass?: ClassValue;
  readonly forceFullscreen?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const zIndex = computed(() => props.zIndex ?? 1000);
const closeAriaLabel = computed(() => props.closeAriaLabel ?? 'Close');
const maxWidthCss = computed(() => {
  if (props.maxWidth === undefined) return undefined;
  return typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth;
});
const popupCardStyle = computed(() => {
  const v = maxWidthCss.value;
  if (!v) return undefined;
  return { '--popup-max-width': v } as unknown as Record<string, string>;
});

function attemptClose(): void {
  if (props.disableClose) return;
  emit('close');
}

useEscapeKey({
  isActive: () => props.show,
  onEscape: attemptClose,
  priority: props.escapePriority ?? 0
});
</script>

<style scoped>
.popup-shell-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  overflow: hidden;
  padding: 16px;
  box-sizing: border-box;
}

.popup-shell-viewport {
  display: flex;
  justify-content: center;
  width: 100%;
  max-height: calc(100vh - 32px);
}

.popup-shell-card {
  width: min(var(--popup-max-width, 900px), 94vw);
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background: #fff;
  box-sizing: border-box;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  transform-origin: center;
  will-change: transform, opacity;
}

.popup-shell-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.popup-shell-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: var(--font-size-header);
  line-height: 1;
  color: #777;
  cursor: pointer;
  padding: 4px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;
}

.popup-shell-close:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #333;
}

.fade-enter-active,
.fade-leave-active {
  transition: none;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .popup-shell-card,
.fade-leave-active .popup-shell-card {
  transition: none;
}

.fade-enter-from .popup-shell-card,
.fade-leave-to .popup-shell-card {
  transform: none;
  opacity: 1;
}

@media (max-width: 900px) {
  .popup-shell-overlay.is-forced-fullscreen {
    padding: 0;
  }

  .popup-shell-viewport.is-forced-fullscreen {
    max-height: 100dvh;
    height: 100dvh;
    align-items: stretch;
  }

  .popup-shell-card.is-forced-fullscreen {
    width: 100vw;
    max-height: 100dvh;
    height: 100dvh;
    border-radius: 0;
    box-shadow: none;
  }
}

@media (max-width: 768px) {
  .popup-shell-overlay {
    padding: 0;
  }

  .popup-shell-viewport {
    max-height: 100dvh;
    height: 100dvh;
    align-items: stretch;
  }

  .popup-shell-card {
    width: 100vw;
    max-height: 100dvh;
    height: 100dvh;
    border-radius: 0;
    box-shadow: none;
  }

  .popup-shell-close {
    top: calc(10px + env(safe-area-inset-top));
    right: calc(10px + env(safe-area-inset-right));
    width: 46px;
    height: 46px;
    font-size: var(--font-size-header);
    background: rgba(0, 0, 0, 0.06);
    color: #222;
  }

  .popup-shell-body {
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
